import { DropdownOption } from '../../../shared/types';

/**
 * Input payload for dynamic dropdown option mapping.
 */
type SetDropdownOptionsProps = {
  field: string;
  options?: DropdownOption[];
};

/**
 * Disables configured options for techno code.
 */
const normalizeDropdownLabel = (value?: string | null): string =>
  (value ?? '').toLowerCase().trim();

// This is a temporary solution to disable specific techno code options based on their labels.
// A more robust solution would be to have this information come from the backend or be part of the dropdown configuration.
// KE0FFMBDCO-7468
const TECHNO_CODE_DISABLED_LABELS = [
  '-',
  '+',
  '0',
  '029995',
  '09',
  '091',
  '092',
  '093',
  '100.1',
  '100.1 - A',
  '100.2',
  '100.2 - A',
  '141',
  '173870',
  '200 LH/RH',
  '200 LH/RH - A',
  '200 LS',
  '200_LS',
  '200 LS - A',
  '29995',
  '3',
  '3D Modell nicht im PLM!',
  '500.1',
  '500.1 - A',
  '500.2',
  '500.2 - A',
  '700',
  '800',
  '800 - A',
  '900.1',
  'Aerocell',
  'Aerocell ',
  'Aerocell (1-3)',
  'Aerocell (4-5)',
  'Aerocell 4-5',
  'Aerocell 4-6',
  'AI-A321-XLR-TSPN-419',
  'Änd.',
  'AS2118-VAR-Einkauf',
  'Auf der Trunnion zu verplanen',
  'Baustufenblatt',
  'BSB',
  'DDMS',
  'DDMS (BtP)',
  'DMU 65',
  'DNC 17/18/19',
  'DNC18',
  'DNC 18',
  'DNC 18/19',
  'DNC 88/89',
  'DNC9 + Aerocell',
  'EMP + Aerocell',
  'FQT',
  'FQT-1000',
  'FQT -900',
  'FQT-900',
  'FQT-900/-1000',
  'Frachter',
  'keine Lieferposition',
  'Loll QSF-A',
  'Make Intern',
  'n/a',
  'OS 091',
  'PC9062-Multi Stage BRA',
  'RCT',
  'RCT Liner',
  'RCT LINER',
  'Reiners, Stephan',
  'S15',
  'S15 - A',
  'Spitzl zu Leuka',
  'SPS',
  'ST5 - 8',
  'ST5-8',
  'ST5 - 8 (Step 4.1)',
  'ST5-8  (Step 4.1)',
  'ST5-8 (Step 4.1)',
  'ST5-8 Step 4.1',
  'ST5-8 u. ST9',
  'ST5-8 u. ST9 (Step 4.1)',
  'ST5 - 9',
  'ST5-9',
  'ST5 - 9 (Step 4.1)',
  'ST5 ST8',
  'ST5, ST8',
  'ST7-8 u. ST9 (Step 4.1)',
  'ST8',
  'ST8 (Step 4.1)',
  'ST9',
  'ST9 ',
  'ST9 (Step 4.1)',
  'STM',
  'STP',
  'STRINGER P44',
  'STS',
  'Swift',
  'SWIFT',
  'Swift FQT Rost',
  'Swift FQT Rost - Assy',
  'Swift FQT Rost - ASSY',
  'Swift FQT Rost - Frästeil (AUG)',
  'Swift FQT Rost - Frästeil (VAR)',
  'Swift FQT Rost - Mini-ASSY (VAR)',
  'Swift FQT Rost - POAT1 Bending',
  'Swift FQT Rost - POAT1 Cut',
  'Swift FQT Rost - POAT1 Mini-ASSY',
  'Swift FQT Rost - POAT1 Profil',
  'Swift FQT Rost - Umformen',
  'T85892',
  'TDA',
  'TLA',
  'TOW_Issue',
  'UGRU 100.1',
  'UGRU 100.2',
  'UGRU 100.2 - A',
  'UGRU 200 LS',
  'UGRU 800',
  'UGRU S15',
  'UGRU S15 - A',
  'W',
  'war TH0186520',
  'XLR FTI',
  'XLR-FTI',
  'Swift FQT Rost - Frästeil',
  '900.2',
  'test',
  'Schweißen'
];

const TECHNO_CODE_DISABLED_LABEL_SET = new Set(
  TECHNO_CODE_DISABLED_LABELS.map(normalizeDropdownLabel),
);

const disableTechnoCodeOptions = (item: DropdownOption): DropdownOption => {
  const label = normalizeDropdownLabel(item?.label);
  const shouldDisable = label ? TECHNO_CODE_DISABLED_LABEL_SET.has(label) : false;
  return shouldDisable ? { ...item, disabled: true } : item;
};

/**
 * Field-specific option transformers.
 * Key: attribute internal name
 * Value: option mapper applied to each option item
 */
const FIELD_OPTION_MAPPERS: Record<string, (item: DropdownOption) => DropdownOption> = {
  techno_code: disableTechnoCodeOptions,
};

/**
 * Applies field-specific dropdown option transformations.
 *
 * If no mapper is registered for the field, the original options are returned.
 *
 * @param props.field Attribute internal name used to resolve a mapper.
 * @param props.options Dropdown options to transform.
 * @returns Transformed options when a mapper exists, otherwise the original options.
 */
export const setNpiSteeringDropdownOptions = ({ field, options = [] }: SetDropdownOptionsProps) => {
  const mapper = FIELD_OPTION_MAPPERS[field];
  return mapper ? options.map(mapper) : options;
};
